<template>
  <div 
    id="monitor-container"
    class="monitor-container"
    :style="containerStyle"
  >
    <!-- Tela de Configurações -->
    <transition name="slide">
      <div v-if="showSettings" class="settings-panel">
        <div class="settings-header">
          <h2>Configurações do Monitor</h2>
          <button @click="showSettings = false" class="close-btn">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="settings-content">
          <!-- Configuração de Logo -->
          <div class="setting-group">
            <label>Logo da Instituição</label>
            <div class="logo-preview">
              <img :src="configuration.logoUrl" alt="Logo" />
            </div>
            <input 
              type="file" 
              @change="handleLogoUpload" 
              accept="image/*"
              class="file-input"
            />
            <input 
              v-model="configuration.logoUrl"
              type="url"
              placeholder="Ou insira uma URL da logo"
              class="url-input"
            />
          </div>

          <!-- Configuração de Cores -->
          <div class="setting-group">
            <label>Cor de Fundo</label>
            <input 
              v-model="configuration.backgroundColor"
              type="color"
              class="color-picker"
            />
          </div>

          <div class="setting-group">
            <label>Cor do Texto</label>
            <input 
              v-model="configuration.textColor"
              type="color"
              class="color-picker"
            />
          </div>

          <!-- Configuração de Modo de Chamada -->
          <div class="setting-group">
            <label>Modo de Chamada</label>
            <select v-model="configuration.callMode" class="select-input">
              <option value="name">Por Nome</option>
              <option value="password">Por Senha</option>
              <option value="both">Nome e Senha</option>
            </select>
          </div>

          <!-- Configurações de Display -->
          <div class="setting-group">
            <label>Tempo de Exibição (segundos)</label>
            <input 
              v-model.number="configuration.displayTime"
              type="number"
              min="5"
              max="60"
              class="number-input"
            />
          </div>

          <div class="setting-group">
            <label>
              <input 
                v-model="configuration.enableSound"
                type="checkbox"
                class="checkbox-input"
              />
              Ativar Som de Chamada
            </label>
          </div>

          <div class="setting-group">
            <label>Volume do Som</label>
            <input 
              v-model.number="configuration.soundVolume"
              type="range"
              min="0"
              max="100"
              :disabled="!configuration.enableSound"
              class="range-input"
            />
            <span>{{ configuration.soundVolume }}%</span>
          </div>

          <!-- Configuração de Salas -->
          <div class="setting-group">
            <label>Salas a Monitorar</label>
            <div class="rooms-selection">
              <label v-for="room in availableRooms" :key="room.id" class="room-checkbox">
                <input 
                  type="checkbox"
                  :value="room.id"
                  v-model="configuration.monitoredRooms"
                />
                {{ room.name }}
              </label>
            </div>
          </div>

          <!-- GIF de Fundo -->
          <div class="setting-group">
            <label>GIF de Fundo (URL)</label>
            <input 
              v-model="configuration.backgroundGifUrl"
              type="url"
              placeholder="https://exemplo.com/background.gif"
              class="url-input"
            />
          </div>

          <!-- Botões de Ação -->
          <div class="settings-actions">
            <button @click="saveConfiguration" class="save-btn">
              Salvar Configurações
            </button>
            <button @click="resetConfiguration" class="reset-btn">
              Restaurar Padrões
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Cabeçalho do Monitor -->
    <header class="monitor-header">
      <div class="logo-container">
        <img 
          :src="configuration.logoUrl" 
          alt="Logo"
          class="logo"
        />
      </div>
      
      <!-- Botões de Controle -->
      <div class="control-buttons">
        <button 
          @click="toggleFullscreen" 
          class="control-btn"
          title="Tela Cheia"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="!isFullscreen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9V4m0 0H4m5 0L4 9m11-5h5m0 0v5m0-5l-5 5m-6 6v5m0 0h5m-5 0l5-5m6 5h5m0 0v-5m0 5l-5-5"></path>
          </svg>
        </button>
        
        <button 
          @click="showSettings = true" 
          class="control-btn"
          title="Configurações"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        </button>
      </div>
    </header>

    <!-- Área Principal de Chamada -->
    <main class="monitor-main" :style="{ color: configuration.textColor }">
      <transition name="fade" mode="out-in">
        <div v-if="currentCall" :key="currentCall.id" class="call-display">
          <!-- Senha (se configurado) -->
          <div v-if="showPassword" class="password-display">
            <span class="password-label">SENHA</span>
            <span class="password-number">{{ currentCall.password || currentCall.sequenceNumber }}</span>
          </div>

          <!-- Nome do Paciente -->
          <div class="patient-name">
            {{ currentCall.patientName }}
          </div>

          <!-- Informações da Sala -->
          <div class="room-info">
            <svg class="room-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
            </svg>
            <span class="room-name">{{ currentCall.roomName }}</span>
          </div>

          <!-- Data e Hora -->
          <div class="datetime-info">
            {{ formatDateTime(currentCall.callTime) }}
          </div>
        </div>

        <!-- Mensagem quando não há chamadas -->
        <div v-else class="no-calls">
          <svg class="no-calls-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p>Aguardando chamadas...</p>
        </div>
      </transition>
    </main>

    <!-- Lista de Chamadas Recentes -->
    <aside v-if="last3RecentCalls.length > 0" class="recent-calls-footer">
      <h3>Últimas 3 Chamadas</h3>
      <div class="recent-calls-list">
        <div 
          v-for="call in last3RecentCalls" 
          :key="call.id"
          class="recent-call-item"
        >
          <span class="recent-patient">{{ call.patientName }}</span>
          <span class="recent-room">{{ call.roomName }}</span>
          <span class="recent-time">{{ formatTime(call.callTime) }}</span>
        </div>
      </div>
    </aside>

    <!-- Área de Estatísticas (usando vue-chartjs) -->
    <div v-if="showStatistics" class="statistics-panel">
      <h3>Estatísticas do Dia</h3>
      <div class="chart-container">
        <canvas ref="statisticsChart"></canvas>
      </div>
    </div>

    <!-- Som de notificação -->
    <audio ref="notificationSound" :src="notificationSoundUrl" preload="auto"></audio>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { Chart, registerables } from 'chart.js'
import { refreshToken } from '@/services/authService'
import { useRouter } from 'vue-router'

// Registrar todos os componentes do Chart.js
Chart.register(...registerables)

const router = useRouter()

// Estado do componente
const showSettings = ref(false)
const isFullscreen = ref(false)
const currentCall = ref(null)
const recentCalls = ref([])
const availableRooms = ref([])
const showStatistics = ref(false)
const statisticsChart = ref(null)
const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
// Configurações do monitor
const configuration = ref({
  logoUrl: '/img/logo.png',
  backgroundColor: '#1a1a1a',
  backgroundGifUrl: '',
  textColor: '#ffffff',
  callMode: 'both', // 'name', 'password', 'both'
  displayTime: 15, // segundos
  enableSound: true,
  soundVolume: 70,
  monitoredRooms: [],
  autoRefreshInterval: 5 // segundos
})

// Referências DOM
const notificationSound = ref(null)
const notificationSoundUrl = '/sounds/notification.mp3'

// Computeds
const showPassword = computed(() => {
  return configuration.value.callMode === 'password' || configuration.value.callMode === 'both'
})

const last3RecentCalls = computed(() => {
  return recentCalls.value.slice(0, 3)
})

const containerStyle = computed(() => {
  const styles = {
    backgroundColor: configuration.value.backgroundColor,
    color: configuration.value.textColor
  }
  if (configuration.value.backgroundGifUrl) {
    styles.backgroundImage = `url('${configuration.value.backgroundGifUrl}')`
    styles.backgroundSize = 'cover'
    styles.backgroundPosition = 'center center'
  } else {
    styles.backgroundImage = 'none'
  }
  return styles
})

// Timers
let displayTimer = null
let refreshTimer = null

// Métodos
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen()
    isFullscreen.value = true
  } else {
    document.exitFullscreen()
    isFullscreen.value = false
  }
}

const handleLogoUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      configuration.value.logoUrl = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

const saveConfiguration = () => {
  localStorage.setItem('monitorConfiguration', JSON.stringify(configuration.value))
  showSettings.value = false
  applyConfiguration()
  
  // Mostrar notificação de sucesso
  showNotification('Configurações salvas com sucesso!')
}

const resetConfiguration = () => {
  configuration.value = {
    logoUrl: '/img/logo.png',
    backgroundColor: '#1a1a1a',
    backgroundGifUrl: '',
    textColor: '#ffffff',
    callMode: 'both',
    displayTime: 15,
    enableSound: true,
    soundVolume: 70,
    monitoredRooms: [],
    autoRefreshInterval: 5
  }
  localStorage.removeItem('monitorConfiguration')
  applyConfiguration()
}

const applyConfiguration = () => {
  // Aplicar volume do som
  if (notificationSound.value) {
    notificationSound.value.volume = configuration.value.soundVolume / 100
  }
  
  // Reiniciar timer de atualização
  clearInterval(refreshTimer)
  
}

const formatDateTime = (dateTime) => {
  const date = new Date(dateTime)
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
  return date.toLocaleDateString('pt-BR', options)
}

const formatTime = (dateTime) => {
  const date = new Date(dateTime)
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const showNotification = (message) => {
  // Criar notificação visual
  const notification = document.createElement('div')
  notification.className = 'notification-toast'
  notification.textContent = message
  notification.style.cssText = `
    position: fixed;
    top: 2rem;
    right: 2rem;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 1rem 2rem;
    border-radius: 0.5rem;
    z-index: 1000;
    font-size: 1rem;
    backdrop-filter: blur(10px);
    animation: slideInFromRight 0.3s ease;
  `
  
  document.body.appendChild(notification)
  
  // Remover após 5 segundos
  setTimeout(() => {
    notification.style.animation = 'slideOutToRight 0.3s ease'
    setTimeout(() => {
      document.body.removeChild(notification)
    }, 300)
  }, 5000)
  
  console.log(message)
}

const loadConfiguration = () => {
  const saved = localStorage.getItem('monitorConfiguration')
  if (saved) {
    configuration.value = JSON.parse(saved)
  }
}

// Lifecycle
onMounted(() => {
  loadConfiguration()

  applyConfiguration()
  
  // Listener para tecla ESC sair do fullscreen
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && showSettings.value) {
      showSettings.value = false
    }
  })
  
  // Listener para mudanças de fullscreen
  document.addEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement
  })
})

onUnmounted(() => {
  clearTimeout(displayTimer)
  clearInterval(refreshTimer)
})

// Watchers
watch(() => configuration.value.enableSound, (newVal) => {
  if (!newVal && notificationSound.value) {
    notificationSound.value.pause()
  }
})

let ws = null
let lastSpokenCallId = null
let isRefreshingToken = false
let reconnectAttempts = 0
const maxReconnectAttempts = 3

// Função para obter o token JWT do cookie
function getCookie(name) {
  const cookies = document.cookie.split('; ')
  for (const cookie of cookies) {
    const [key, value] = cookie.split('=')
    if (key === name) return decodeURIComponent(value)
  }
  return null
}

async function conectarWebSocket() {
  try {
    // Obter o token JWT
    const token = getCookie('access_token')
    let wsUrl = `${protocol}://${window.location.host}/socket/monitor`
    
    // Se há token, adiciona como parâmetro na URL
    if (token) {
      wsUrl += `?token=${encodeURIComponent(token)}`
      console.log('Token encontrado, conectando WebSocket com autenticação...')
    } else {
      console.warn('Nenhum token encontrado para autenticação do WebSocket')
    }
    
    console.log('URL do WebSocket:', wsUrl.replace(/token=[^&]+/, 'token=***'))
    ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      console.log('WebSocket conectado com sucesso!')
      reconnectAttempts = 0 // Reset counter on successful connection
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        
        // Verificar se é erro de autenticação
        if (msg.error && msg.error.includes('403') || msg.status === 403) {
          console.warn('WebSocket erro 403 - token expirado')
          handleAuthError()
          return
        }
        
        // Processar apenas notificações de chamada
        const eventType = msg.type || (msg.data && msg.data.type)
        if (eventType !== 'call_notification') return
        const payload = msg.data || msg  // aceita ambos formatos

        // Estrutura esperada: id / patient_name ou patientName etc.
        const call = {
          id: payload.id || payload.call_id || Date.now(),
          patientName: payload.patient_name || payload.patientName || 'Paciente',
          roomName: payload.room_name || payload.roomName || 'Sala',
          password: payload.password || payload.sequence_number || payload.sequenceNumber || null,
          sequenceNumber: payload.sequence_number || payload.sequenceNumber || null,
          callTime: payload.call_time || payload.callTime || new Date().toISOString()
        }

        // Coloca como chamada atual
        currentCall.value = call

        // Apenas speak se novo evento de chamada
        if (call.id !== lastSpokenCallId) {
          lastSpokenCallId = call.id
          const speakCall = () => {
            if ('speechSynthesis' in window) {
              // Aguardar a fala anterior terminar antes de iniciar uma nova
              if (window.speechSynthesis.speaking) {
                // Se já está falando, não iniciar nova fala
                return
              }
              
              // Garantir que o nome está limpo de caracteres especiais que possam causar corte
              const cleanPatientName = call.patientName?.trim() || ''
              const cleanRoomName = call.roomName?.trim() || ''
              const textToSpeak = `${cleanPatientName} - ${cleanRoomName}`
              
              const utterance = new SpeechSynthesisUtterance(textToSpeak)
              
              // Configurar para melhor qualidade e evitar corte inicial
              utterance.rate = 0.9
              utterance.pitch = 1.0
              utterance.volume = 1.0
              
              // Pequeno delay para garantir que o texto está pronto
              setTimeout(() => {
                // Verificar novamente se não há fala em andamento
                if (!window.speechSynthesis.speaking) {
                  window.speechSynthesis.speak(utterance)
                }
              }, 100)
            }
          }
          // Apenas fala, sem tocar sino
          speakCall()
        }

        // Limpa timer anterior e define novo
        clearTimeout(displayTimer)
        displayTimer = setTimeout(() => {
          // Move para recentes
          recentCalls.value.unshift(call)
          // Limita a 3 itens
          recentCalls.value = recentCalls.value.slice(0, 3)
          currentCall.value = null
        }, (configuration.value.displayTime || 15) * 1000)
      } catch(e){
        console.error('Erro ao processar mensagem WS', e)
      }
    }

    ws.onerror = (event) => {
      console.error('WebSocket erro:', event)
      // Se o erro acontecer durante o handshake inicial, pode ser 403
      if (ws.readyState === WebSocket.CONNECTING) {
        console.warn('Erro durante handshake do WebSocket - possível problema de autenticação')
      }
    }

    ws.onclose = (event) => {
      console.warn('WebSocket fechado:', event.code, event.reason)
      
      // Se for erro de autenticação (código 4003 = custom auth error, 1008 = policy violation, 1006 = abnormal closure)
      if (event.code === 4003 || event.code === 1008 || event.code === 1006 || event.reason?.includes('403')) {
        console.error('Erro de autenticação detectado no WebSocket:', event.code)
        handleAuthError()
        return
      }
      
      // Reconectar após alguns segundos se não for erro de auth
      if (reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++
        console.log(`Tentativa de reconexão ${reconnectAttempts}/${maxReconnectAttempts}`)
        setTimeout(conectarWebSocket, 3000 * reconnectAttempts)
      } else {
        console.error('Máximo de tentativas de reconexão atingido')
        showNotification('Erro de conexão. Verifique sua autenticação.')
      }
    }
  } catch (error) {
    console.error('Erro ao criar WebSocket:', error)
    if (reconnectAttempts < maxReconnectAttempts) {
      reconnectAttempts++
      setTimeout(conectarWebSocket, 3000 * reconnectAttempts)
    }
  }
}

async function handleAuthError() {
  if (isRefreshingToken) {
    console.log('Refresh já em andamento, aguardando...')
    return // Já está tentando refresh
  }
  
  console.log('Iniciando processo de refresh do token devido a erro de autenticação')
  isRefreshingToken = true
  
  // Verificar se ainda temos um token
  const currentToken = getCookie('access_token')
  if (!currentToken) {
    console.error('Nenhum token encontrado, redirecionando para login')
    isRefreshingToken = false
    showNotification('Sessão expirada. Redirecionando para login...')
    setTimeout(() => {
      router.push({ name: 'Login', query: { sessionExpired: true } })
    }, 2000)
    return
  }
  
  try {
    console.log('Tentando refresh do token...')
    const tokenData = await refreshToken()
    
    if (tokenData && tokenData.access_token) {
      console.log('Token renovado com sucesso, reconectando WebSocket...')
      showNotification('Sessão renovada, reconectando...')
      
      // Fechar conexão atual se existir
      if (ws) {
        ws.close()
        ws = null
      }
      
      // Aguardar um pouco antes de reconectar
      setTimeout(() => {
        isRefreshingToken = false
        reconnectAttempts = 0
        conectarWebSocket()
      }, 1500)
    } else {
      throw new Error('Refresh token retornou dados inválidos')
    }
  } catch (error) {
    console.error('Erro ao renovar token:', error.response?.data || error.message || error)
    isRefreshingToken = false
    
    // Se o refresh falhar, redirecionar para login
    showNotification('Sessão expirada. Redirecionando para login...')
    setTimeout(() => {
      router.push({ name: 'Login', query: { sessionExpired: true } })
    }, 2000)
  }
}

onMounted(() => {
  conectarWebSocket()
})

onUnmounted(() => {
  // Limpar WebSocket
  if (ws) {
    ws.close()
    ws = null
  }
  
  // Limpar timers
  clearTimeout(displayTimer)
  clearInterval(refreshTimer)
  
  // Reset flags
  isRefreshingToken = false
  reconnectAttempts = 0
})
</script>

<style scoped>
/* Container Principal */
.monitor-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  transition: background-color 0.3s ease;
}

/* Header */
.monitor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  position: relative;
  z-index: 10;
}

.logo-container {
  flex: 1;
  display: flex;
  justify-content: center;
}

.logo {
  max-height: 80px;
  max-width: 300px;
  object-fit: contain;
}

.control-buttons {
  position: absolute;
  right: 2rem;
  top: 2rem;
  display: flex;
  gap: 1rem;
}

.control-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

/* Main Content */
.monitor-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.call-display {
  text-align: center;
  animation: fadeIn 0.5s ease;
}

.password-display {
  margin-bottom: 2rem;
}

.password-label {
  display: block;
  font-size: 2rem;
  font-weight: 300;
  margin-bottom: 0.5rem;
  opacity: 0.8;
}

.password-number {
  display: block;
  font-size: 6rem;
  font-weight: 700;
  line-height: 1;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.patient-name {
  font-size: 4rem;
  font-weight: 600;
  margin-bottom: 2rem;
  line-height: 1.2;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.room-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  font-size: 3rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.room-icon {
  width: 3rem;
  height: 3rem;
}

.datetime-info {
  font-size: 1.5rem;
  opacity: 0.8;
  font-weight: 300;
}

.no-calls {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0.5;
}

.no-calls-icon {
  width: 6rem;
  height: 6rem;
  margin-bottom: 1rem;
  opacity: 0.3;
  animation: spin 2s linear infinite;
  transform-origin: center center;
}

/* Recent Calls Footer */
.recent-calls-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  flex-direction: column;
  padding: 1rem 2rem;
  backdrop-filter: blur(8px);
  max-height: 35vh;
  overflow-y: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.recent-calls-footer h3 {
  color: white;
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  opacity: 0.9;
  text-align: left;
  padding-left: 0.5rem;
}

.recent-calls-footer .recent-calls-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.recent-calls-footer .recent-call-item {
  background: rgba(255,255,255,0.05);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: white;
  font-size: 0.95rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s;
}

.recent-calls-footer .recent-call-item:hover {
    background: rgba(255, 255, 255, 0.1);
}

.recent-patient {
  font-weight: 600;
  flex: 1;
}

.recent-room {
  opacity: 0.8;
  margin: 0 1rem;
  min-width: 120px;
  text-align: right;
}

.recent-time {
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
}

/* Settings Panel */
.settings-panel {
  position: absolute;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow-y: auto;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.settings-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #e5e7eb;
  color: #111827;
}

.settings-content {
  padding: 1.5rem;
}

.setting-group {
  margin-bottom: 2rem;
}

.setting-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.logo-preview {
  width: 100%;
  height: 120px;
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: #f9fafb;
}

.logo-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.file-input,
.url-input,
.select-input,
.number-input {
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.file-input:focus,
.url-input:focus,
.select-input:focus,
.number-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.color-picker {
  width: 60px;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  cursor: pointer;
}

.checkbox-input {
  margin-right: 0.5rem;
}

.range-input {
  width: 100%;
  margin-top: 0.5rem;
}

.rooms-selection {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.room-checkbox {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  cursor: pointer;
}

.room-checkbox:hover {
  background: #f3f4f6;
}

.settings-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.save-btn,
.reset-btn {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.save-btn {
  background: #3b82f6;
  color: white;
  border: none;
}

.save-btn:hover {
  background: #2563eb;
}

.reset-btn {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.reset-btn:hover {
  background: #f3f4f6;
}

/* Statistics Panel */
.statistics-panel {
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  width: 400px;
  height: 300px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  padding: 1.5rem;
}

.statistics-panel h3 {
  color: white;
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.chart-container {
  width: 100%;
  height: calc(100% - 2rem);
}

/* Animações */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutToRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .settings-panel {
    width: 100%;
  }
  
  .patient-name {
    font-size: 2.5rem;
  }
  
  .room-info {
    font-size: 2rem;
  }
  
  .password-number {
    font-size: 4rem;
  }
  
  .recent-calls-footer {
    display: none;
  }
  
  .statistics-panel {
    display: none;
  }
}

/* Dark theme adjustments */
@media (prefers-color-scheme: dark) {
  .settings-panel {
    background: #1f2937;
    color: #f3f4f6;
  }
  
  .settings-header {
    border-bottom-color: #374151;
  }
  
  .settings-header h2 {
    color: #f3f4f6;
  }
  
  .close-btn {
    background: #374151;
    color: #9ca3af;
  }
  
  .close-btn:hover {
    background: #4b5563;
    color: #f3f4f6;
  }
  
  .setting-group label {
    color: #d1d5db;
  }
  
  .file-input,
  .url-input,
  .select-input,
  .number-input {
    background: #374151;
    border-color: #4b5563;
    color: #f3f4f6;
  }
  
  .logo-preview {
    background: #374151;
    border-color: #4b5563;
  }
  
  .rooms-selection {
    background: #374151;
    border-color: #4b5563;
  }
  
  .room-checkbox:hover {
    background: #4b5563;
  }
  
  .reset-btn {
    background: #374151;
    color: #f3f4f6;
    border-color: #4b5563;
  }
  
  .reset-btn:hover {
    background: #4b5563;
  }
}
</style>
